import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  loadReport,
  loadChild,
  updateReportContent,
  approveReport,
  getRenderedPdfDownloadUrl,
} from "../lib/firestore";
import { getAccessToken } from "../lib/auth";
import { generateDraft } from "../lib/draftApi";
import { renderReport } from "../lib/renderApi";
import { ageDisplay } from "../types";
import type { Report, Child } from "../types";
import { FramingNoteInput } from "./FramingNoteInput";
import { EylfChips } from "./EylfChips";

type Props = { uid: string };

type Content = Report["content"];

export function EditorPage({ uid }: Props) {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [child, setChild] = useState<Child | null>(null);
  const [content, setContent] = useState<Content | null>(null);
  const [framingNote, setFramingNote] = useState<string>("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Pre-fetched download URL — when the report is rendered, we resolve
  // the signed URL once on load so the Download button can be a real
  // <a href> link (synchronous click → no popup blocker on mobile).
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId) return;
    (async () => {
      const r = await loadReport(uid, reportId);
      if (!r) {
        setError("Report not found");
        return;
      }
      setReport(r);
      setContent(r.content);
      setFramingNote(r.vera_framing_note ?? "");
      setChild(await loadChild(uid, r.child_id));
      if (r.status === "rendered" && r.pdf_storage_path) {
        try {
          setPdfUrl(await getRenderedPdfDownloadUrl(r.pdf_storage_path));
        } catch (err) {
          console.error("Pre-fetch PDF URL failed:", err);
        }
      }
    })();
  }, [uid, reportId]);

  if (error) return <main className="p-4 text-red-600">{error}</main>;
  if (!report || !content || !child) return <main className="p-4 text-gray-500">Loading…</main>;

  const updateField = (key: keyof Content, value: string | null) => {
    setContent({ ...content, [key]: value });
  };
  const updateCodes = (codes: string[]) => setContent({ ...content, eylf_codes: codes });

  const isEmptyDraft = content.story_en === "";

  const handleGenerate = async () => {
    setBusy("generate");
    setError(null);
    try {
      // Persist framing note + any local edits BEFORE calling /draft.
      // Otherwise Vera's typed observation lives only in React state
      // and Gemini reads `null` from Firestore — silently degrading
      // the draft. This is the framing-note-loss bug from code review.
      await updateReportContent(uid, report.id, content, framingNote || null);
      const accessToken = await getAccessToken();
      const result = await generateDraft({ reportId: report.id, pickerAccessToken: accessToken });
      setContent({
        ...result.draft,
        evaluation_en: result.draft.evaluation_en || null,
        evaluation_zh: result.draft.evaluation_zh || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Draft failed");
    } finally {
      setBusy(null);
    }
  };

  const handleSave = async () => {
    setBusy("save");
    setError(null);
    try {
      await updateReportContent(uid, report.id, content, framingNote || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(null);
    }
  };

  const handleApprove = async () => {
    setBusy("approve");
    setError(null);
    try {
      await updateReportContent(uid, report.id, content, framingNote || null);
      await approveReport(uid, report.id);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const handleRender = async () => {
    setBusy("render");
    setError(null);
    try {
      const accessToken = await getAccessToken();
      const result = await renderReport({ reportId: report.id, pickerAccessToken: accessToken });
      const refreshed = await loadReport(uid, report.id);
      if (refreshed) {
        setReport(refreshed);
        setContent(refreshed.content);
      }
      // Don't auto-open — store the URL and let the user click the link
      // (synchronous click bypasses mobile Safari popup blocker).
      // Use the same getDownloadURL mechanism as pre-rendered reports —
      // /render no longer returns a signed URL, just the path.
      setPdfUrl(await getRenderedPdfDownloadUrl(result.pdf_storage_path));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Render failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="p-4 max-w-3xl mx-auto space-y-6">
      <header className="border-b pb-3">
        <h1 className="text-xl font-semibold">
          {child.name_en} / {child.name_zh} ({ageDisplay(child.date_of_birth)})
        </h1>
        <p className="text-sm text-gray-500">{report.report_period}</p>
      </header>

      <FramingNoteInput value={framingNote} onChange={setFramingNote} />

      {isEmptyDraft && (
        <button
          onClick={handleGenerate}
          disabled={busy !== null}
          className="bg-[#ff6b35] text-white rounded-full px-6 py-3 font-medium disabled:opacity-50"
        >
          {busy === "generate" ? "Drafting (15–30s)…" : "Generate AI draft"}
        </button>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Field label="The learning story" value={content.story_en}
          onChange={(v) => updateField("story_en", v)} />
        <Field label="學習故事" value={content.story_zh}
          onChange={(v) => updateField("story_zh", v)} />
        <Field label="Analysis of learning" value={content.analysis_en}
          onChange={(v) => updateField("analysis_en", v)} />
        <Field label="學習分析" value={content.analysis_zh}
          onChange={(v) => updateField("analysis_zh", v)} />
        <Field label="Learning outcomes" value={content.learning_outcomes_en}
          onChange={(v) => updateField("learning_outcomes_en", v)} />
        <Field label="學習成果" value={content.learning_outcomes_zh}
          onChange={(v) => updateField("learning_outcomes_zh", v)} />
        <Field label="What next" value={content.what_next_en}
          onChange={(v) => updateField("what_next_en", v)} />
        <Field label="下一步的計劃" value={content.what_next_zh}
          onChange={(v) => updateField("what_next_zh", v)} />
        <Field label="Evaluation" value={content.evaluation_en ?? ""}
          onChange={(v) => updateField("evaluation_en", v || null)} />
        <Field label="評估孩子學習進程" value={content.evaluation_zh ?? ""}
          onChange={(v) => updateField("evaluation_zh", v || null)} />
      </div>

      <section>
        <h3 className="text-sm font-medium mb-2">EYLF Outcomes</h3>
        <EylfChips selected={content.eylf_codes} onChange={updateCodes} />
      </section>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <footer className="flex gap-3 pt-4 border-t flex-wrap">
        <button
          onClick={handleSave}
          disabled={busy !== null}
          className="text-gray-700 border rounded-full px-4 py-2 disabled:opacity-50"
        >
          {busy === "save" ? "Saving…" : "Save draft"}
        </button>
        {report.status === "draft" && (
          <button
            onClick={handleApprove}
            disabled={busy !== null || isEmptyDraft}
            className="bg-[#ff6b35] text-white rounded-full px-6 py-2 font-medium disabled:opacity-50"
          >
            {busy === "approve" ? "Approving…" : "Approve"}
          </button>
        )}
        {(report.status === "approved" || report.status === "rendered") && (
          <button
            onClick={handleRender}
            disabled={busy !== null}
            className="bg-[#ff6b35] text-white rounded-full px-6 py-2 font-medium disabled:opacity-50"
          >
            {busy === "render"
              ? "Rendering (5–10s)…"
              : report.status === "rendered" ? "Re-render PDF" : "Render PDF"}
          </button>
        )}
        {report.status === "rendered" && pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#ff6b35] underline rounded-full px-4 py-2 self-center"
          >
            Download PDF ↓
          </a>
        )}
      </footer>
    </main>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <textarea
        rows={6}
        className="block w-full border rounded px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
